import React, { useState, useEffect } from 'react';
import { Table, Card, Typography, Button, Space, Input, Modal, Form, InputNumber, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { formatCurrency } from '../utils/formatters';
import { inventoryService, StockLevel, StockAdjustment } from '../services/inventory';

const { Title } = Typography;
const { Search } = Input;

export const Inventory: React.FC = () => {
  const [stockLevels, setStockLevels] = useState<StockLevel[]>([]);
  const [loading, setLoading] = useState(false);
  const [adjustModalVisible, setAdjustModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchStockLevels();
  }, []);

  const fetchStockLevels = async () => {
    setLoading(true);
    try {
      const response = await inventoryService.getStockLevels();
      setStockLevels(response.data);
    } catch (error) {
      message.error('獲取庫存信息失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustStock = async (values: StockAdjustment) => {
    try {
      await inventoryService.adjustStock(values);
      message.success('庫存調整成功');
      setAdjustModalVisible(false);
      form.resetFields();
      fetchStockLevels();
    } catch (error) {
      message.error('庫存調整失敗');
    }
  };

  const columns = [
    {
      title: '商品名稱',
      dataIndex: 'product_name',
      key: 'product_name',
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
    },
    {
      title: '當前庫存',
      dataIndex: 'current_stock',
      key: 'current_stock',
      render: (value: number) => (
        <span style={{ color: value < 10 ? '#f5222d' : '#52c41a' }}>
          {value}
        </span>
      ),
    },
    {
      title: '低庫存閾值',
      dataIndex: 'low_stock_threshold',
      key: 'low_stock_threshold',
    },
    {
      title: '庫存狀態',
      dataIndex: 'is_low_stock',
      key: 'is_low_stock',
      render: (isLowStock: boolean) => (
        <span style={{ color: isLowStock ? '#f5222d' : '#52c41a' }}>
          {isLowStock ? '低庫存' : '正常'}
        </span>
      ),
    },
    {
      title: '總價值',
      dataIndex: 'total_value',
      key: 'total_value',
      render: (value: number) => formatCurrency(value, 'NT$'),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>庫存管理</Title>
          <Space>
            <Search
              placeholder="搜索庫存"
              style={{ width: 200 }}
              onSearch={(value) => {
                // TODO: 實現搜索功能
              }}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAdjustModalVisible(true)}
            >
              庫存調整
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={stockLevels}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 項`,
          }}
        />
      </Card>

      <Modal
        title="庫存調整"
        open={adjustModalVisible}
        onCancel={() => setAdjustModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAdjustStock}
        >
          <Form.Item
            name="product_id"
            label="商品"
            rules={[{ required: true, message: '請選擇商品' }]}
          >
            <Input placeholder="請輸入商品ID" />
          </Form.Item>

          <Form.Item
            name="quantity"
            label="調整數量"
            rules={[{ required: true, message: '請輸入調整數量' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="正數為入庫，負數為出庫"
            />
          </Form.Item>

          <Form.Item
            name="type"
            label="調整類型"
            rules={[{ required: true, message: '請選擇調整類型' }]}
          >
            <Input placeholder="入庫/出庫" />
          </Form.Item>

          <Form.Item
            name="reason"
            label="調整原因"
            rules={[{ required: true, message: '請輸入調整原因' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                確認調整
              </Button>
              <Button onClick={() => setAdjustModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
